import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import {
  DomainSelector,
  Modal,
  useGlobalState,
  useLocalStorage,
  useMessage,
} from '@pepper/ui'
import { attachDomainWithProject } from '@pepper/utils/src/api/seo'

import { useSelectedCompanyId } from 'utils/functions/misc'

import s from './LinkDomainModal.module.css'

type LinkDomainModalProps = {
  isOpen: boolean
  onClose: () => void
}

export const LinkDomainModal = ({ isOpen, onClose }: LinkDomainModalProps) => {
  const companyId = useSelectedCompanyId()
  const queryClient = useQueryClient()
  const { projectId } = useParams<{ projectId: string }>()
  const { domainId } = useGlobalState()
  const [lsDomainId] = useLocalStorage('domain_id', '')

  const [selectedDomainId, setSelectedDomainId] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { showSuccess, showError } = useMessage()

  const handleSubmit = async () => {
    if (selectedDomainId) {
      setIsLoading(true)
      const { status, error } = await attachDomainWithProject({
        companyId,
        projectId,
        domainId: selectedDomainId,
      })
      if (status === 'ok') {
        showSuccess('Domain linked successfully')
        await queryClient.invalidateQueries({
          queryKey: ['project-domain'],
        })
        await queryClient.invalidateQueries({
          queryKey: ['project-topics'],
        })
        await queryClient.invalidateQueries({
          queryKey: ['project-keywords'],
        })
        await queryClient.invalidateQueries({
          queryKey: ['project-domain-map'],
        })
        onClose()
      } else {
        showError(error ?? 'Unable to attach domain. Please try again')
      }
      setIsLoading(false)
    } else {
      showError('Please select a domain')
    }
  }

  useEffect(() => {
    if (domainId) {
      setSelectedDomainId(domainId)
    } else if (lsDomainId) {
      setSelectedDomainId(lsDomainId)
    }
  }, [lsDomainId, domainId])

  return (
    <Modal
      title="Link a Domain with the Project"
      size="sm"
      visible={isOpen}
      destroyOnClose={true}
      onCancel={onClose}
      okText="Continue"
      onOk={handleSubmit}
      okButtonProps={{
        disabled: !selectedDomainId,
        loading: isLoading,
      }}
    >
      <span className={s.label}>Select a domain</span>
      <DomainSelector
        companyId={companyId}
        size="regular"
        isControlled={true}
        value={selectedDomainId}
        onChange={_domainId => setSelectedDomainId(_domainId)}
      />
    </Modal>
  )
}
